'use client';

import { useEffect, useState } from 'react';
import detectMimeType from '../utils/detect-mime-type';
const base64ToUint8Array = (base64Data: string) => {
  const binaryString = atob(base64Data);
  const bytes = new Uint8Array(binaryString.length);
  for (let i = 0; i < binaryString.length; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }
  return bytes;
};

// Decrypted uploads sometimes arrive wrapped in an HTTP header block: either a multipart
// boundary (`--<boundary>` of arbitrary length) or a bare `Content-Type:`/`Content-Disposition:`
// header line. Strip everything up to the blank-line separator so the real file bytes surface.
// Lowercased comparison because HTTP/2 sends header names in lowercase.
const HEADER_PREFIXES = ['content-type:', 'content-disposition:'];
const startsWithHeaderBlock = (bytes: Uint8Array): boolean => {
  if (bytes[0] === 0x2d && bytes[1] === 0x2d) return true; // "--" (multipart boundary)
  const head = String.fromCharCode(...bytes.subarray(0, 20)).toLowerCase();
  return HEADER_PREFIXES.some(prefix => head.startsWith(prefix));
};
const stripMultipartHeaders = (bytes: Uint8Array): Uint8Array<ArrayBuffer> => {
  if (!startsWithHeaderBlock(bytes)) {
    return new Uint8Array(bytes);
  }

  // Find the blank line separator (double CRLF or double LF) which separates headers from content
  for (let i = 0; i < Math.min(bytes.length - 4, 1000); i++) {
    if (bytes[i] === 0x0d && bytes[i + 1] === 0x0a && bytes[i + 2] === 0x0d && bytes[i + 3] === 0x0a) {
      return new Uint8Array(bytes.subarray(i + 4));
    }
    if (bytes[i] === 0x0a && bytes[i + 1] === 0x0a) {
      return new Uint8Array(bytes.subarray(i + 2));
    }
  }
  return new Uint8Array(bytes);
};
const useObjectUrl = (base64Data: string | null) => {
  const [result, setResult] = useState<{
    objectUrl: string | null;
    mimeType: string | null;
  }>({
    objectUrl: null,
    mimeType: null
  });
  useEffect(() => {
    if (!base64Data) {
      setResult({
        objectUrl: null,
        mimeType: null
      });
      return;
    }
    try {
      const rawBytes = base64ToUint8Array(base64Data);
      const stripped = stripMultipartHeaders(rawBytes);
      // Only trust the strip if it exposed a recognizable file; otherwise it likely cut into
      // legitimate content (e.g. text starting with `--`), so fall back to the original bytes.
      const strippedMime = detectMimeType(stripped);
      const useStripped = strippedMime !== 'application/octet-stream';
      const bytes = useStripped ? stripped : rawBytes;
      const mime = useStripped ? strippedMime : detectMimeType(rawBytes);
      const blob = new Blob([bytes], {
        type: mime
      });
      const url = window.URL.createObjectURL(blob);
      setResult({
        objectUrl: url,
        mimeType: mime
      });
      return () => {
        window.URL.revokeObjectURL(url);
      };
    } catch (error) {
      // If base64 decoding fails, return null so caller can use fallback
      console.warn('Failed to create object URL from base64 data:', error);
      setResult({
        objectUrl: null,
        mimeType: null
      });
    }
  }, [base64Data]);
  return result;
};
export default useObjectUrl;